import FilterBox from '../filter-box';
import FilterBase from '../filter-base';
import SizeOption, { SizeChildOption } from './size-option';
import { FilterActiveValue } from '../active-filters';
import { show, hide, removeClass, addClass } from '../../common';
import FilterParamStats from '../filter-stats';
import { FilterParamItem } from '../filter-param-items';
import SizeOptionGroup from './size-option-group';

enum ToggleState{
    collapsed = 'collapsed', expanded = 'expanded'
}

export default class TextileSizeFilter extends FilterBase {
    private options: SizeOptionGroup[];
    private showMoreLink: HTMLElement;
    private showMoreText: string;
    private showLessText: string;
    private moreItemsElement: HTMLElement;
    private mainValuesListId: number;
    private mainGroupWrapper: HTMLElement;

    constructor(baseElement: HTMLElement, filterBox: FilterBox) {
        super(baseElement, filterBox);
        this.mainValuesListId = Number(baseElement.dataset.mainValuesListId);
        this.mainGroupWrapper = baseElement.querySelector('[data-component-part="main-group-wrapper"]');
    }

    init(): void {
        this.initOptions();
        this.initShowMoreLink();
    }

    private initOptions() {
        const options = [...this.baseElement.querySelectorAll('[data-component-part="group-box"]')]
            .map(x => new SizeOptionGroup(x as HTMLElement, this.pageId, this.paramId, () => this.updateAttributeAndApplyChanges()));
        this.options = options;
    }

    protected setValuesInternal(values: string): Promise<void> {
        const selectedValues = values.split(',')
        selectedValues.forEach(id => {
            for (let i = 0; i < this.options.length; i++) {
                const optionGroup = this.options[i];
                const option = optionGroup.options.find(i => i.listId === id);
                if(option){
                    option.select();
                    if(!option.isMainItem && this.isShowMoreCollapsed()){
                        this.setShowMoreVisibility(ToggleState.expanded);
                    }
                }
            }
        });
        return Promise.resolve();
    }

    protected getSelectedValuesConcat(): string {
        return this.getAllSelectedValues().join(',');
    }
    
    protected getActiveFilterValues(): FilterActiveValue[] {

        let selectedValues: FilterActiveValue[] = [];
        let selectedsIzeOptionsValues: SizeOption[] = [];
        this.options.forEach(x => {
            x.options.forEach(o => {
                if (o.isSelected) {
                    selectedsIzeOptionsValues.push(o);
                }
                const selectedChildren = o.children
                    .filter(s => s.isSelected)
                    .map(s => <FilterActiveValue>{ value: s.listId, displayName: s.name });
                if (selectedChildren && selectedChildren.length > 0) {
                    selectedValues = selectedValues.concat(selectedChildren);
                }
            });

        });

        if (selectedsIzeOptionsValues) {
            selectedValues = selectedValues.concat(selectedsIzeOptionsValues.map(s => <FilterActiveValue>{
                value: s.listId, displayName: s.name
            }));
        }        

        return selectedValues;
    }

    protected clearValuesInternal(): void {
        this.options.forEach(x => {
            x.options.forEach(o => o.unselect());
        });
    }

    public unselect(value: string): void {
        this.options.forEach(x => {
            const topLevelItem = x.options.find(o => o.listId === value);
            if (topLevelItem) {
                topLevelItem.unselect();
            } else {
                const subSize = this.getChildSizeByListId(value);
                if (subSize) {
                    subSize.unselect();
                }
            }

            this.updateAttributeAndApplyChanges();        
        });
    }

    private getChildSizeByListId(listId: string): SizeChildOption {
        let childSize: SizeChildOption = null;
        this.options.forEach(x => {
            for (let i = 0; i < x.options.length; i++) {
                const subSize = x.options[i].children.find(o => o.listId === listId);
                if (subSize) childSize = subSize;
            }
        });
        return childSize;
    }


    public setStats(stats: FilterParamStats): void {
        const items = stats.Items;

        if(!items || items.length === 0){
            hide(this.baseElement);
            return;
        }
        show(this.baseElement);
        
        this.options.forEach(o => o.updateItems(items));

        const itemsToAdd = items.filter(i => !this.options.find(o => o.listId === i.ListId.toString()));
        this.addOptions(itemsToAdd);

        this.updateActiveFilterValues();
    }

    private addOptions(items: FilterParamItem[]){
        const optionsToAdd = items
            .map(i => SizeOptionGroup.createOptionGroupElement(i, i.ListId === this.mainValuesListId))
            .map(e => new SizeOptionGroup(e, this.pageId, this.paramId, () => this.updateAttributeAndApplyChanges()));

        if(optionsToAdd.length !== 0){
            this.options = this.options.concat(optionsToAdd);
            this.sortOptionElements();
        }
    }

    private sortOptionElements() {
        const mainGroup = this.options.find(x => x.listId === this.mainValuesListId.toString());
        if(mainGroup && !this.mainGroupWrapper.hasChildNodes()){
            this.mainGroupWrapper.appendChild(mainGroup.element);
        }

        if(!this.moreItemsElement){
            return;
        }

        while (this.moreItemsElement.firstChild) {
            this.moreItemsElement.removeChild((this.moreItemsElement.lastChild));
        }

        const otherGroups = this.options
            .filter(x => x.listId !== this.mainValuesListId.toString())
            .sort((a,b) => a.sortValue - b.sortValue);

        otherGroups.forEach(o => this.moreItemsElement.appendChild(o.element));
    }

    private initShowMoreLink(): void {
        this.showMoreLink = this.baseElement.querySelector('[data-component-part="show-more-link"]');
        if(this.showMoreLink){
            this.moreItemsElement = this.baseElement.querySelector('[data-component-part="more-items"]');
            this.showMoreLink.addEventListener('click', () => this.toggleShowMore());
            this.showMoreText = this.showMoreLink.innerText;
            this.showLessText = this.showMoreLink.dataset.showLessText;
        }
    }

    private toggleShowMore(): void {
        this.setShowMoreVisibility(this.isShowMoreCollapsed() ? ToggleState.expanded : ToggleState.collapsed);
    }

    private setShowMoreVisibility(toggleState: ToggleState): void {
        if(toggleState === ToggleState.expanded){
            show(this.moreItemsElement);
            this.showMoreLink.dataset.toggleState = toggleState.toString();
            this.showMoreLink.innerText = this.showLessText;
            removeClass(this.showMoreLink, 'show-more-link');
            addClass(this.showMoreLink, 'show-less-link');
        } else {
            hide(this.moreItemsElement);
            this.showMoreLink.dataset.toggleState = toggleState.toString();
            this.showMoreLink.innerText = this.showMoreText;
            removeClass(this.showMoreLink, 'show-less-link');
            addClass(this.showMoreLink, 'show-more-link');
        }
    }

    private isShowMoreCollapsed(): boolean {
        if(!this.showMoreLink) 
            return true;
        return this.showMoreLink.dataset.toggleState === ToggleState.collapsed.toString();
    }

    private getAllSelectedValues(): string[]{

        let selectedValues: string[] = [];
        this.options.forEach(o => {

            o.options.forEach(subItem => {
                if (subItem.isSelected) {
                    selectedValues.push(subItem.listId);
                }
                const selectedChildren = subItem.children
                    .filter(s => s.isSelected)
                    .map(s => s.listId);
                if (selectedChildren) {
                    selectedValues = selectedValues.concat(selectedChildren);
                }
            }); 

           
        });

        return selectedValues;
    }

}
